import { logger } from './logger';
import { notify } from './notifications';
import { getErrorMessage, isMissingIndexError } from './errorUtils';
import { withRetry, RetryOptions } from './networkUtils';

export interface ErrorHandlingOptions {
    silent?: boolean;
    rethrow?: boolean;
    /**
     * Whether to automatically retry the operation (default: false for services to avoid double retries)
     */
    enableRetry?: boolean;
    /**
     * Options for the retry logic
     */
    retryOptions?: RetryOptions;
}

/**
 * Standardizes error logging, message extraction, and user notifications for services.
 * 
 * @param error - The error object or unknown value caught
 * @param context - Human-readable description of what failed (e.g., 'loading clients', 'saving profile')
 * @param options - Optional configuration
 */
export function handleServiceError(
    error: unknown,
    context: string,
    options: ErrorHandlingOptions = {}
): void {
    const { silent = false } = options;

    // 1. Standardize logging
    const logError = (error instanceof Error || (typeof error === 'object' && error !== null))
        ? error
        : new Error(String(error));
    logger.error(context, logError);

    // 2. Extract user-friendly message
    const errorMessage = getErrorMessage(error);

    // 3. Handle Permission Denied specifically
    const isPermissionError =
        errorMessage.toLowerCase().includes('permission') ||
        (error as { code?: string })?.code === 'permission-denied';

    // 4. Notify User. Errors the calling screen shows inline themselves (e.g. the
    // New Request HIPAA refusal, F-P1-2) opt out so the user sees one message.
    const handledInline = (error as { handledInline?: unknown } | null)?.handledInline === true;
    if (!silent && !handledInline) {
        if (isMissingIndexError(error)) {
            // PR-3: never show Firestore's raw index message or its console link.
            notify.error(`Couldn't ${context} with these filters right now. Clear the filters or try again later.`);
        } else if (isPermissionError) {
            notify.error(
                `Permission denied: You do not have permission to ${context}. Please contact support if you believe this is an error.`
            );
        } else {
            notify.error(`Failed to ${context}: ${errorMessage}`);
        }
    }
}

/**
 * Wraps an async function with standard service error handling.
 * 
 * @param fn - The async function to execute
 * @param context - Human-readable description of what failed
 * @param options - Optional configuration
 */
export async function withErrorHandling<T>(
    fn: () => Promise<T>,
    context: string,
    options: ErrorHandlingOptions = {}
): Promise<T | undefined> {
    const { rethrow = true, enableRetry = false, retryOptions } = options;

    const operation = enableRetry
        ? () => withRetry(fn, retryOptions)
        : fn;

    try {
        return await operation();
    } catch (error: unknown) {
        handleServiceError(error, context, options);
        if (rethrow) {
            throw error;
        }
        return undefined;
    }
}
